import React, { useState, ReactElement, useCallback, useMemo } from 'react';
import { Pie } from '@nivo/pie';
import { PieChartData } from '@Types/nivo';
import './ByAttributeTile.scss';

interface ByAttributeTileProps {
    title: string;
    numTasks?: number;
    data: PieChartData[];
}

const CHART_SIZE = 134;

// Mirrors the `tableau10` scheme handed to <Pie colors>. Nivo builds an ordinal scale over
// the datum ids in the order the arcs are computed — which is the order of `data` — so the
// nth legend swatch is the colour of the nth slice. Kept local rather than imported from
// @nivo/colors, which is only a transitive dependency here.
const TABLEAU10 = [
    '#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f',
    '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab',
];

interface LegendEntry {
    key: string;
    label: string;
    value: number;
    color: string;
    percentage: string;
}

// One decimal only when the value is not whole, so the common case reads "25%" not "25.0%".
const formatPercentage = (value: number, total: number): string => {
    if (!total) return '0';
    const pct = (value / total) * 100;
    return pct.toFixed(1).replace(/\.0$/, '');
};

const ByAttributeTile = (props: ByAttributeTileProps): ReactElement => {
    const [isGrown, setIsGrown] = useState<boolean>(false);
    const [rotation, setRotation] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragStart, setDragStart] = useState<{ x: number } | null>(null);

    const toggleGrowing = useCallback((): void => {
        setIsGrown((prev) => !prev);
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
        setIsDragging(true);
        setDragStart({ x: e.clientX });
    }, []);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
        if (!isDragging || !dragStart) return;
        const deltaX = e.clientX - dragStart.x;
        setRotation((prev) => prev + deltaX * 0.5);
    }, [isDragging, dragStart]);

    const handleMouseUp = useCallback((): void => {
        setIsDragging(false);
        setDragStart(null);
    }, []);

    const innerRadius = useMemo(() => (isGrown ? 0.4 : 0.7), [isGrown]);
    const tileClassName = useMemo(
        () => `by-attribute-tile ${isGrown ? 'grow' : 'shrink'}`,
        [isGrown]
    );

    // The API does not send a percentage, so derive it from the slice values.
    const total = useMemo(
        () => props.data.reduce((sum, datum) => sum + (datum.value ?? 0), 0),
        [props.data]
    );

    const legend: LegendEntry[] = useMemo(
        () => props.data.map((datum, index) => ({
            key: String(datum.id),
            label: datum.label ?? String(datum.id),
            value: datum.value,
            color: TABLEAU10[index % TABLEAU10.length],
            percentage: formatPercentage(datum.value, total),
        })),
        [props.data, total]
    );

    const percentageById = useMemo(() => {
        const map = new Map<string, string>();
        legend.forEach((entry) => map.set(entry.key, entry.percentage));
        return map;
    }, [legend]);

    const renderTooltip = useCallback(({ datum }: { datum: { id: string | number; label: React.ReactNode; value: number; color: string } }) => (
        <div className='by-attribute-tile-tooltip'>
            <span className='by-attribute-tile-tooltip-swatch' style={{ background: datum.color }} />
            <span className='by-attribute-tile-tooltip-label'>{datum.label}</span>
            <span className='by-attribute-tile-tooltip-value'>
                {datum.value} ({percentageById.get(String(datum.id)) ?? '0'}%)
            </span>
        </div>
    ), [percentageById]);

    return (
        <div className={tileClassName}>
            <div className='by-attribute-tile-header'>
                <div className='by-attribute-tile-header-title'>{props.title}</div>
                <div className='by-attribute-tile-header-actions'>
                    <i className='fa-regular fa-ellipsis'></i>
                    <i
                        className={`fa-regular ${isGrown ? 'fa-arrow-left-from-line' : 'fa-arrow-right-from-line'}`}
                        onClick={toggleGrowing}
                    ></i>
                </div>
            </div>
            <div
                className='by-attribute-tile-body'
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
                <Pie
                    height={CHART_SIZE}
                    width={CHART_SIZE}
                    data={props.data}
                    innerRadius={innerRadius}
                    colors={{ scheme: 'tableau10' }}
                    enableArcLabels={false}
                    // Expanded tiles carry the labels in the legend below instead — arc link
                    // labels at this chart size collide with each other once there are more
                    // than a handful of slices.
                    enableArcLinkLabels={false}
                    startAngle={rotation}
                    endAngle={rotation + 360}
                    animate={true}
                    isInteractive={true}
                    tooltip={renderTooltip}
                />
            </div>
            {isGrown && legend.length > 0 && (
                <div className='by-attribute-tile-legend'>
                    {legend.map((entry) => (
                        <div
                            className='by-attribute-tile-legend-item'
                            key={entry.key}
                            title={`${entry.label}: ${entry.value} (${entry.percentage}%)`}>
                            <span className='by-attribute-tile-legend-swatch' style={{ background: entry.color }} />
                            <span className='by-attribute-tile-legend-label'>{entry.label}</span>
                            <span className='by-attribute-tile-legend-percentage'>{entry.percentage}%</span>
                        </div>
                    ))}
                </div>
            )}
            <div className='by-attribute-tile-footer'>
                {props.numTasks !== undefined && (
                    <>
                        <div className='by-attribute-tile-footer-icon'>
                            <i className='fa-regular fa-bolt'></i>
                        </div>
                        <div className='by-attribute-tile-footer-text'>{props.numTasks} Tasks</div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ByAttributeTile;
